import { useState, useEffect } from "react";
import { getJoinRequests, handleJoinRequest } from "../api/mintSpace";
import { useTranslation } from "../context/TranslationContext";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import UserProfileLink from "../components/UserProfileLink";

const MintSpaceRequestsPage = ({ isDark, setToast, mintSpaceId }) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!mintSpaceId) return;
      try {
        setLoading(true);
        const response = await getJoinRequests(mintSpaceId);
        if (response.success) {
          setRequests(response.data || []);
        }
      } catch (error) {
        setToast(t("toasts.failedLoadJoinRequests"));
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [mintSpaceId, setToast, t]);

  const handleRequest = async (requestId, action) => {
    try {
      const response = await handleJoinRequest(mintSpaceId, requestId, action);
      if (response.success) {
        setToast(action === "accept" ? t("toasts.requestAcceptedSuccess") : t("toasts.requestRejectedSuccess"));
        // Remove request from list
        setRequests((prev) => prev.filter((req) => req._id !== requestId));
      }
    } catch (error) {
      setToast(error.response?.data?.message || (action === "accept" ? t("toasts.failedAcceptRequest") : t("toasts.failedRejectRequest")));
    }
  };

  if (loading) {
    return <p className="py-space-40 text-center text-subheadline text-label-secondary">{t("common.loading")}</p>;
  }

  if (requests.length === 0) {
    return (
      <div className="m-space-8 rounded-radius-lg bg-bg-raised lg:m-0">
        <EmptyState icon="users" title={t("messages.noPendingRequests")} />
      </div>
    );
  }

  return (
    <section className="m-space-8 flex flex-col divide-y divide-separator-default rounded-radius-lg bg-bg-raised font-ds lg:m-0">
      {requests.map((request) => (
        <article key={request._id} className="flex flex-col gap-space-12 p-space-16">
          <div className="flex flex-wrap items-center gap-space-12">
            <UserProfileLink
              user={request.requester}
              avatarSize="lg"
              isDark={isDark}
              nameClassName="text-headline text-label-primary"
              fallback="Unknown User"
            />
            <p className="text-footnote text-label-secondary">{new Date(request.createdAt).toLocaleDateString()}</p>
            <span className="flex-1" />
            <Button size="s" variant="gray" onClick={() => handleRequest(request._id, "reject")}>
              {t("mintSpace.reject")}
            </Button>
            <Button size="s" icon={<Icon name="check" size={16} />} onClick={() => handleRequest(request._id, "accept")}>
              {t("mintSpace.approve")}
            </Button>
          </div>
          {request.answers?.length > 0 && (
            <div className="flex flex-col gap-space-8 rounded-[22px] bg-bg-inset p-space-16">
              {request.answers.map((answer, index) => (
                <div key={index}>
                  <p className="text-subheadline-semibold text-label-primary">{answer.question}</p>
                  <p className="text-subheadline text-label-secondary">{answer.answer}</p>
                </div>
              ))}
            </div>
          )}
        </article>
      ))}
    </section>
  );
};

export default MintSpaceRequestsPage;
